<template>
  <div class="px-2 py-4 inventory-transfer-container">
    <v-overlay v-if="loading" opacity="0.7" color="grey darken-4">
      <div class="text-center">
        <v-progress-circular indeterminate size="64" color="primary" class="mb-3"></v-progress-circular>
        <div class="white--text font-weight-medium">Cargando Documento...</div>
      </div>
    </v-overlay>

    <v-fade-transition>
      <div v-if="!loading">
        <!-- Banner Informativo de Bloqueo por Documento Aplicado -->
        <v-alert
          v-if="dataDoc.sts === 'A'"
          type="info"
          color="success"
          dark
          border="left"
          elevation="2"
          class="mb-6 rounded-lg font-weight-medium"
          icon="mdi-lock"
        >
          Este documento de transferencia ha sido aplicado a inventario. Los datos y el listado de productos se encuentran bloqueados para su edición.
        </v-alert>

        <!-- Main Card Container -->
        <v-card class="elevation-3 rounded-lg overflow-hidden">
          
          <!-- Card Header & Navigation Section -->
          <v-card-text class="grey lighten-4 py-4 px-6 border-bottom">
            <v-row align="center" justify="space-between">
              <v-col cols="12" sm="auto" class="d-flex align-center">
                <v-icon large color="primary" class="mr-3">mdi-swap-horizontal-bold</v-icon>
                <div>
                  <h1 class="text-h5 font-weight-bold grey--text text--darken-3 mb-1">
                    {{ title }}
                  </h1>
                  <span class="text-caption grey--text text--darken-1">
                    Transferencia de Mercancías e Inventario
                  </span>
                </div>
              </v-col>

              <v-col cols="12" sm="auto" class="d-flex flex-wrap align-center gap-2">
                <!-- Status Badge -->
                <v-chip
                  :color="dataDoc.sts === 'A' ? 'success' : 'amber darken-2'"
                  dark
                  small
                  class="font-weight-bold px-3 py-4 mr-2 elevation-1"
                >
                  <v-icon left small>
                    {{ dataDoc.sts === 'A' ? 'mdi-check-decagram' : 'mdi-file-document-edit' }}
                  </v-icon>
                  {{ dataDoc.sts === 'A' ? 'Aplicado' : 'Borrador' }}
                </v-chip>

                <!-- Back Button -->
                <v-btn
                  color="grey darken-2"
                  outlined
                  class="rounded-md px-4"
                  :loading="saving"
                  @click="close()"
                >
                  <v-icon left>mdi-arrow-left</v-icon>
                  Regresar
                </v-btn>

                <!-- Save Button -->
                <v-btn
                  v-if="dataDoc.sts != 'A'"
                  color="success"
                  depressed
                  class="rounded-md px-4"
                  :loading="saving"
                  @click="guardar()"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Guardar
                </v-btn>

                <!-- Acuse (Print) Button -->
                <v-btn
                  v-if="idDocumento"
                  color="primary"
                  outlined
                  class="rounded-md px-4"
                  @click="printTicket()"
                  :loading="saving"
                >
                  <v-icon left>mdi-printer</v-icon>
                  Acuse
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>

          <v-divider></v-divider>

          <!-- Warehouse Flow Selection (Origin & Destination) -->
          <v-card-text class="pa-6">
            <v-row>
              <!-- Origin Warehouse Card -->
              <v-col cols="12" md="5">
                <v-card outlined class="origin-card rounded-lg pa-4 h-100" style="border-top: 4px solid #f57c00 !important;">
                  <div class="d-flex align-center mb-4">
                    <v-avatar color="orange lighten-5" size="40" class="mr-3">
                      <v-icon color="orange darken-3">mdi-export</v-icon>
                    </v-avatar>
                    <div>
                      <h3 class="text-subtitle-1 font-weight-bold orange--text text--darken-3 mb-0">
                        Almacén de Salida
                      </h3>
                      <span class="text-caption grey--text">Origen de la transferencia</span>
                    </div>
                  </div>

                  <v-select
                    :items="modulos"
                    label="Departamento origen"
                    v-model="dataDoc.from.dpt"
                    outlined
                    dense
                    hide-details
                    class="mb-4"
                    :disabled="dataDoc.sts === 'A'"
                  ></v-select>

                  <v-autocomplete
                    label="Almacén origen"
                    v-model="dataDoc.from.amc"
                    :items="origenes"
                    item-text="Nombre"
                    item-value="id"
                    outlined
                    dense
                    hide-details
                    :disabled="dataDoc.sts === 'A'"
                  />
                </v-card>
              </v-col>

              <!-- Central Arrow Indicator -->
              <v-col cols="12" md="2" class="d-flex align-center justify-center py-4">
                <div class="text-center d-none d-md-block">
                  <v-avatar color="grey lighten-3" size="48">
                    <v-icon color="grey darken-2" large>mdi-arrow-right-bold</v-icon>
                  </v-avatar>
                </div>
                <div class="text-center d-block d-md-none py-1">
                  <v-avatar color="grey lighten-3" size="40">
                    <v-icon color="grey darken-2">mdi-arrow-down-bold</v-icon>
                  </v-avatar>
                </div>
              </v-col>

              <!-- Destination Warehouse Card -->
              <v-col cols="12" md="5">
                <v-card outlined class="destination-card rounded-lg pa-4 h-100" style="border-top: 4px solid #2e7d32 !important;">
                  <div class="d-flex align-center mb-4">
                    <v-avatar color="green lighten-5" size="40" class="mr-3">
                      <v-icon color="green darken-3">mdi-import</v-icon>
                    </v-avatar>
                    <div>
                      <h3 class="text-subtitle-1 font-weight-bold green--text text--darken-3 mb-0">
                        Almacén de Destino
                      </h3>
                      <span class="text-caption grey--text">Destino de la transferencia</span>
                    </div>
                  </div>

                  <v-select
                    :items="modulos"
                    label="Departamento destino"
                    v-model="dataDoc.to.dpt"
                    outlined
                    dense
                    hide-details
                    class="mb-4"
                    :disabled="dataDoc.sts === 'A'"
                  ></v-select>

                  <v-autocomplete
                    label="Almacén destino"
                    v-model="dataDoc.to.amc"
                    :items="origenes"
                    item-text="Nombre"
                    item-value="id"
                    outlined
                    dense
                    hide-details
                    :disabled="dataDoc.sts === 'A'"
                  />
                </v-card>
              </v-col>
            </v-row>

            <!-- Apply to Inventory Button (Draft + Saved Document only) -->
            <v-row v-if="dataDoc.sts != 'A' && idDocumento" class="mt-4">
              <v-col cols="12">
                <v-btn
                  color="warning"
                  dark
                  depressed
                  large
                  block
                  :loading="saving"
                  @click="saveSend()"
                  class="rounded-lg font-weight-bold py-6 text-uppercase letter-spacing"
                >
                  <v-icon left class="mr-2">mdi-checkbox-marked-circle-outline</v-icon>
                  Aplicar e impactar inventario
                </v-btn>
              </v-col>
            </v-row>

            <v-divider class="my-6"></v-divider>

            <!-- Product Form Section -->
            <div class="product-form-section">
              <!-- If active draft & origin warehouse selected: Show search form -->
              <v-card v-if="dataDoc.sts != 'A' && this.dataDoc.from.amc" outlined class="rounded-lg pa-4 mb-6 grey lighten-5">
                <div class="d-flex align-center mb-4">
                  <v-icon color="primary" class="mr-2">mdi-plus-circle-outline</v-icon>
                  <span class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Agregar Insumos o Productos</span>
                </div>
                <productoExistForm
                  :productsDocument="productsDocument"
                  :Origen="dataDoc.from.amc"
                  :loadData="newLoadData"
                  :key="formExistKey"
                  @toggle="showModalExist = false"
                  @successData="saveExistProduct"
                >
                </productoExistForm>
              </v-card>

              <!-- If no origin warehouse selected yet -->
              <div v-else-if="dataDoc.sts != 'A'" class="text-center py-6 px-4 mb-6 border-dashed rounded-lg grey lighten-5">
                <v-icon large color="grey lighten-1" class="mb-2">mdi-warehouse</v-icon>
                <div class="text-subtitle-2 grey--text text--darken-1">
                  Seleccione un Almacén de Origen/Salida para comenzar a agregar productos.
                </div>
              </div>
            </div>

            <!-- Products List Section -->
            <div class="products-list-section">
              <div class="d-flex align-center justify-between mb-4 flex-wrap">
                <div class="d-flex align-center">
                  <v-icon color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
                  <span class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Listado de Artículos a Transferir</span>
                </div>
                <v-spacer></v-spacer>
                <span class="text-caption grey--text font-weight-bold" v-if="productsDocument.length > 0">
                  Total de productos principales: {{ productsDocument.length }}
                </span>
              </div>

              <!-- EMPTY STATE -->
              <div v-if="renderedData.length === 0" class="d-flex flex-column align-center justify-center py-10 px-4 text-center border-dashed rounded-lg">
                <v-avatar size="80" color="primary lighten-5" class="mb-4">
                  <v-icon size="40" color="primary">mdi-package-variant-closed</v-icon>
                </v-avatar>
                <h3 class="text-h6 font-weight-bold grey--text text--darken-2 mb-1">
                  No hay productos en esta transferencia
                </h3>
                <p class="text-body-2 grey--text text--darken-1 max-width-xs mb-0">
                  Use el panel superior para buscar, escanear y agregar insumos o presentaciones a transferir.
                </p>
              </div>

              <!-- PRODUCTS TABLE -->
              <div v-else class="table-responsive rounded-lg overflow-hidden border">
                <v-simple-table class="custom-transfer-table">
                  <thead>
                    <tr>
                      <th class="text-center py-3" style="width: 120px;">Cantidad</th>
                      <th class="text-left py-3">Presentación / Producto</th>
                      <th class="text-left py-3" style="width: 180px;">Rendimiento</th>
                      <th class="text-center py-3" style="width: 100px;" v-if="dataDoc.sts != 'A'">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="item in renderedData">
                      <!-- Group Header Row (Main Product) -->
                      <tr
                        v-if="item.type === 'header'"
                        :key="item.Id"
                        class="header-row font-weight-bold"
                      >
                        <td colspan="4" class="py-3 px-4">
                          <div class="d-flex align-center flex-wrap">
                            <!-- Product Type Tag -->
                            <v-chip
                              :color="item.clr"
                              x-small
                              label
                              outlined
                              class="mr-2 font-weight-bold px-2 py-3"
                            >
                              <v-icon x-small left>{{ item.icn }}</v-icon>
                              {{ item.tp }}
                            </v-chip>

                            <span class="text-subtitle-2 font-weight-bold grey--text text--darken-3">
                              {{ item.nm }}
                            </span>
                            
                            <v-spacer></v-spacer>
                            
                            <!-- Total Product Accumulator -->
                            <v-chip
                              color="grey darken-2"
                              dark
                              x-small
                              class="font-weight-bold px-2 py-3"
                            >
                              Total: {{ item.ct }} {{ getUnidadMedida(item.Id) }}
                            </v-chip>
                          </div>
                        </td>
                      </tr>

                      <!-- Presentation Child Row -->
                      <tr
                        v-else
                        :key="item.Id"
                        class="presentation-row"
                      >
                        <td class="text-center py-2 font-weight-bold text-body-2">
                          {{ item.cn }}
                        </td>
                        <td class="text-left py-2 pl-6">
                          <span class="grey--text text--darken-2">{{ item.nm }}</span>
                        </td>
                        <td class="text-left py-2 text-body-2 grey--text">
                          {{ item.rm }} {{ item.um }}
                        </td>
                        <td class="text-center py-2" v-if="dataDoc.sts != 'A'">
                          <v-btn
                            icon
                            small
                            color="red darken-1"
                            @click="deleteByPresentacion(item.Id)"
                            class="hover-danger"
                            title="Eliminar presentación"
                          >
                            <v-icon small>mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </v-simple-table>
              </div>
            </div>

          </v-card-text>
        </v-card>
      </div>
    </v-fade-transition>
  </div>
</template>

<style scoped>
.inventory-transfer-container {
  max-width: 1200px;
  margin: 0 auto;
}
.menubtn {
  display: none;
}
.border-bottom {
  border-bottom: 1px solid #e0e0e0 !important;
}
.border-dashed {
  border: 2px dashed #cfd8dc !important;
}
.max-width-xs {
  max-width: 320px;
}
.gap-2 {
  gap: 8px;
}
.letter-spacing {
  letter-spacing: 0.8px;
}
.custom-transfer-table {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}
.theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  background-color: #f5f7f8;
  color: #37474f !important;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
  border-bottom: 2px solid #cfd8dc !important;
}
.header-row {
  background-color: #f1f5f9 !important;
  border-top: 1px solid #e2e8f0;
}
.presentation-row {
  background-color: #ffffff;
  border-bottom: 1px solid #f1f5f9;
}
.presentation-row:hover {
  background-color: #f8fafc !important;
}
.hover-danger:hover {
  background-color: #ffebee !important;
}
.origin-card, .destination-card {
  transition: all 0.2s ease-in-out;
  border: 1px solid #e0e0e0 !important;
}
.origin-card:hover, .destination-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}
</style>

<script>
import moment from "moment";
import pdfmake from "pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
pdfmake.vfs = pdfFonts.pdfMake.vfs;
import transferTicket from "../../../clases/PdfMakers/transferTicket.js";
import productoExistForm from "../TransferenciaMercancias/ProductExistForm.vue";
export default {
  name: "DocumentosCreator",
  components: {
    productoExistForm,
  },
  props: [
    "idDocumento",
    "deptoOrigen",
    "almacenOrigen",
    "deptoDestino",
    "almacenDestino",
  ],
  data() {
    return {
      tableKey: 0,
      columnDefs: null,
      saving: false,
      selection: [],
      dataDoc: {
        from: {
          dpt: null,
          amc: null,
        },
        to: {
          dpt: null,
          amc: null,
        },
        sts: "C",
      },
      Origen: "",
      loading: false,
      Aplicado: false,
      title: "Nuevo Documento de transferencia de inventarios",
      code: "",
      showModal: false,
      showModalExist: false,
      showModalFactura: false,
      formKey: 0,
      formExistKey: 0,
      newLoadData: false,
      formFacturaKey: 0,
      idsSelected: [],
      productsDocument: [],
      reg: {},
      formIndex: 0,
      editMode: false,
      editData: [],
      hoteles: [],
      usuarios: [],
      editedHotel: {},
      config: {
        selectableRow: true,
        selectConfig: {
          multiSelect: true,
          idRowData: "id", // id necesario para indicar id unico del row que esta seleccionado y retornarlo en los eventos requeridos
        },
        orderByCol: { Field: "", Type: "Asc" },
        buttons: [
          {
            text: "Eliminar",
            icon: "lni lni-trash",
            buttonId: "deleteDocumentProduct",
            require: true,
            limitRequire: 1,
          },
        ],
        fields: [
          {
            text: "Nombre",
            value: "nm",
          },
          {
            text: "Cantidad a transferir",
            value: "cn",
          },
          {
            text: "Unidad de medida",
            value: "id",
            format: (row) => {
              let existProduct = this.$pgsHelper.getById(
                this.productsDocument,
                "id",
                row.id
              );
              if (existProduct) {
                return existProduct?.Extra?.AYB?.UM || "PZA";
              }
            },
          },
        ],
      },
    };
  },
  methods: {
    printTicket() {
      this.saving = true;
      this.dataDoc.cto = {};
      this.productsDocument.forEach((element) => {
        element.cn = Number(element.cn);
        this.dataDoc.cto[element.id] = element;
      });
      this.dataDoc.reg = this.reg;
      this.$store.getters.getFirebaseApp.fba
        .call(
          "AMC-TransferenciasInventario-updateTransferenciaInventario",
          this.dataDoc
        )
        .then((result) => {
          let mainData = {
            TITLE: "Transferencia Inventario",
            FI: this.dataDoc?.mta?.cra?.t
              ? new moment(this.dataDoc?.mta?.cra?.t).format("DD/MM/YYYY")
              : new moment().format("DD/MM/YYYY"),
            //FF: this.input_date_final,
            FG: new moment().format("DD/MM/YYYY"),
            HG: new moment().format("HH:mm:ss"),
            AS: this.$pgsHelper.getById(
              this.origenes,
              "id",
              this.dataDoc.from.amc
            ).Nombre,
            DS: this.$pgsHelper.getById(
              this.modulos,
              "value",
              this.dataDoc.from.dpt
            ).text,
            AE: this.$pgsHelper.getById(
              this.origenes,
              "id",
              this.dataDoc.to.amc
            ).Nombre,
            DE: this.$pgsHelper.getById(
              this.modulos,
              "value",
              this.dataDoc.to.dpt
            ).text,
            groupedData: this.renderedData,
            data: this.dataDoc,
          };
          let docData = transferTicket.getDocData(mainData);
          pdfmake.createPdf(docData).open();
          this.saving = false;
        })
        .catch((error) => {
          this.saving = false;
          this.$swal(`Request failed: ${error}`, "", "error");
        });
    },
    getUnidadMedida(idProducto) {
      let exist = this.$pgsHelper.getById(
        this.$store.getters.getProductos,
        "id",
        idProducto
      );
      if (exist) {
        if (exist.Extra?.AYB?.UM) {
          return exist.Extra?.AYB?.UM;
        } else {
          return "PZA";
        }
      }
      //return exist
    },
    deleteByPresentacion(idPres) {
      if (this.reg[idPres]) {
        let totalBorrar = this.reg[idPres].cn * this.reg[idPres].rm;
        let existInDoc = this.$pgsHelper.getById(
          this.productsDocument,
          "id",
          this.reg[idPres].pi
        );
        if (existInDoc) {
          this.productsDocument[existInDoc.idx].cn =
            this.productsDocument[existInDoc.idx].cn - totalBorrar;
          if (this.productsDocument[existInDoc.idx].cn <= 0) {
            this.productsDocument.splice(existInDoc.idx, 1);
          }
        }
        delete this.reg[idPres];
        this.$forceUpdate();
      }
    },
    // grouperRenderedData() {
    //   //let orderByProd = this.$_.orderBy(this.reg,'pi','asc');
    //   let arrayPres = Object.entries(this.reg);
    //   let presentaciones = [];
    //   arrayPres.forEach((pres) => {
    //     presentaciones.push({ Id: pres[0], ...pres[1] });
    //   });
    //   let orderByProd = this.$_.orderBy(presentaciones, "pi", "asc");
    //   let presentacionesOrdered = [];
    //   let auxiliaHeader = "";
    //   try {
    //     orderByProd.forEach((pres) => {
    //       if (auxiliaHeader != pres.pi) {
    //         let producto = this.$pgsHelper.getById(
    //           this.productsDocument,
    //           "id",
    //           pres.pi
    //         );
    //         if (producto) {
    //           presentacionesOrdered.push({
    //             type: "header",
    //             nm: producto.nm,
    //             ct: producto.cn,
    //             Id: pres.pi,
    //           });
    //         }
    //       }
    //       presentacionesOrdered.push(pres);
    //       auxiliaHeader = pres.pi;
    //     });
    //     return presentacionesOrdered;
    //   } catch (e) {
    //     console.log(e);
    //   }
    // },
    close() {
      this.$emit("close", !this.value);
      // if(this.idDocumento){
      //   window.close();
      //   this.goRoute('/productos-inventarios/transferencias')
      // }
    }, 
    goRoute(routePath) {
      this.$router.push({ path: routePath });
    },
    ongridReady: function (params) {
      let gridApi = params.api;
      gridApi.sizeColumnsToFit();
      let gridColumnApi = params.columnApi;
    },
    onSelectionChanged(params) {
      const selectedRows = params.api.getSelectedRows();

      this.selection = selectedRows;
    },
    dtButtonClick(buttonId, idSelected) {
      if (this.Aplicado) {
        this.$swal(
          "No puedes modificar un documento aplicado a inventario",
          "",
          "error"
        );
        return false;
      }
      if (buttonId == "modificarFactura") {
        this.formFacturaKey++;
        this.idsSelected = idSelected;
        this.showModalFactura = true;
      }
      if (buttonId == "deleteDocumentProduct") {
        this.deleteItem(idSelected);
      }
    },
    saveExistProduct(datos) {
      /*if (this.productsDocument.length > 450) {
        this.$swal(
          "No puedes agregar mas de 450 productos al documento",
          "",
          "error"
        );
        return false;
      }*/
      let existProduct = this.$pgsHelper.getById(
        this.productsDocument,
        "id",
        datos.producto
      );
      if (existProduct) {
        this.productsDocument[existProduct.idx].cn += Number(datos.cantidad);
      } else {
        this.productsDocument.push({
          id: datos.producto,
          _e: true,
          cn: Number(datos.cantidad),
          nm: datos.nombre,
        });
      }
      if (datos.presentacionData) {
        if (!this.reg[datos.presentacionData.Id]) {
          this.reg[datos.presentacionData.Id] = {
            nm: datos.presentacionData.nom,
            /** Cantidad */
            cn: datos.presentacionData.cntScanner,
            /** Rendimiento */
            rm: datos.presentacionData.rdm,
            /** Unidad de medida */
            um: datos.presentacionData.um,
            /** Producto */
            pi: datos.presentacionData.prd,
          };
        } else {
          if (datos.presentacionData.Id.includes("PGR_")) {
            this.reg[datos.presentacionData.Id].rm +=
              datos.presentacionData.rdm;
          } else {
            this.reg[datos.presentacionData.Id].cn +=
              datos.presentacionData.cntScanner;
          }
        }
      }
      this.tableKey++;
      this.showModalExist = false;
    }, 
    toggleModal(open) {
      this.showModal = open;
    },
    verifySelectedOrigen(callback) {
      if (!this.dataDoc.from.amc) {
        this.$swal("Seleccione un centro de consumo", "", "error");
        return false;
      }
      {
        callback();
      }

      return true;
    },
    async getDocumentInfo(id) {
      console.time("getDocumentInfo");
      this.loading = true;
      this.productos = [];
      this.productsDocument = [];
      const firestore = this.$store.getters.getFirebaseApp.firestore;
      const doc = firestore.doc("AMC_TransferenciasInventario/", id);
      const data = await firestore.getDoc(doc);
      console.timeEnd("getDocumentInfo");

      // this.$pgsHelper.log(data.docs.map(doc=>{return {id:doc.ref.id,...doc.data()}}));
      
      let document = { id: data.ref.id, ...data.data() };
      this.dataDoc = document;
      if (!document.cto) {
        document.cto = {};
      }
      if (document.reg) {
        this.reg = document.reg;
      }
      let productos = Object.entries(document.cto);
      productos.forEach((element) => {
        let datos = element[1];
        this.productsDocument.push({
          id: element[0],
          ...datos,
        });
      });
      this.loading = false;
    },
    guardar(sendInv = false) {
      if (
        this.dataDoc.from.amc == null ||
        this.dataDoc.to.amc == null ||
        this.dataDoc.from.dpt == null ||
        this.dataDoc.to.dpt == null
      ) {
        this.$swal(
          "Seleccione los almacenes de origen y destino",
          "Para continuar por favor indique el centro de consumo al que iran los productos",
          "error"
        );
        return false;
      }
      this.dataDoc.cto = {};
      this.productsDocument.forEach((element) => {
        element.cn = Number(element.cn);
        this.dataDoc.cto[element.id] = element;
      });
      this.dataDoc.reg = this.reg;
      if (this.idDocumento) {
        this.editDocument(this.dataDoc, sendInv);
      } else {
        this.saveDocument(this.dataDoc, sendInv);
      }
    },
    saveSend() {
      this.guardar(true);
    },
    sendInventario() {
      let datos = {
        Id: this.idDocumento,
      };
      this.$swal({
        title: "¿Deseas afectar el inventario con los productos indicados?",
        text: " El documento sera enviado a inventario ",
        icon: "warning",
        allowOutsideClick: false,
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Guardar",
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          return await this.$store.getters.getFirebaseApp.fba
            .call(
              "AMC-TransferenciasInventario-applyTransferenciaInventario",
              datos
            )
            .then(function (result) {
              return result;
            })
            .catch((error) => {
              this.$swal.showValidationMessage(`Request failed: ${error}`);
            });
        },
      }).then((result) => {
        if (result.value) {
          if (result.value.data.ok) {
            this.$pgsHelper.writeLog({
              Evento: "INVENTARIO_TRASPASO_APLICAR",
              Mensaje: `Traspaso de inventario aplicado: ${this.idDocumento}`,
              reference: { id: this.idDocumento, tp: "INV-TraspasoMercancia", al: "TRASPASO INVENTARIO" },
              data: datos
            });
            this.getDocumentInfo(this.idDocumento);
            this.$swal({
              title: "Guardado",
              text: "El documento se envio a inventario corretamente",
              icon: "success",
            });
            this.Aplicado = true;
          } else {
            this.$swal(
              "Error aplicando inventario",
              result.value.data.error,
              "error"
            );
          }
        }
      });
    },
    deleteItem(id) {
      this.$swal({
        title: "¿Deseas eliminar este producto del documento?",
        text: "Los cambios se veran reflejados hasta realizar el guardado ",
        icon: "warning",
        showCancelButton: true,
        allowOutsideClick: false,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Eliminar",
        showLoaderOnConfirm: true,
      }).then((result) => {
        if (result.value) {
          this.productsDocument.forEach((element, index) => {
            if (element.id == id) {
              this.productsDocument.splice(index, 1);
            }
          });
          this.$swal({
            title: "Eliminado",
            text: "El producto se elimino corretamente",
            icon: "success",
          });
        }
      });
    },
    saveDocument(datos, sendInv) {
      this.$swal({
        title:
          "¿Deseas crear este nuevo documento con los productos indicados?",
        text: "Se registrar el documento y sus productos indicados ",
        icon: "warning",
        allowOutsideClick: false,
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Guardar",
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          return await this.$store.getters.getFirebaseApp.fba
            .call(
              "AMC-TransferenciasInventario-createTransferenciaInventario",
              datos
            )
            .then(function (result) {
              return result;
            })
            .catch((error) => {
              this.$swal.showValidationMessage(`Request failed: ${error}`);
            });
        },
      }).then((result) => {
        if (result.value) {
          if (result.value.data.ok) {
            const targetId = result.value.data.data?.Id || result.value.data.id;
            this.$pgsHelper.writeLog({
              Evento: "INVENTARIO_TRASPASO_CREATE",
              Mensaje: `Documento de traspaso creado: ${targetId || ""}`.trim(),
              reference: { id: targetId, tp: "INV-TraspasoMercancia", al: "TRASPASO INVENTARIO" },
              data: datos
            });
            this.getDocumentInfo(result.value.data.data.Id);
            this.$swal({
              title: "Guardado",
              text: "El documento se guardo corretamente",
              icon: "success",
            });
            // this.$router.push({
            //   path:
            //     "/productos-inventarios/documentoTransferencia/" +
            //     result.value.data.data.Id,
            // });
            // this.close()
            if (sendInv) {
              this.sendInventario();
            }
          } else {
            this.$swal(
              "Error saving Documento",
              result.value.data.error,
              "error"
            );
          }
        }
      });
    },
    editDocument(datos, sendInv) {
      this.saving = true;
      datos.Id = this.idDocumento;

      this.$store.getters.getFirebaseApp.fba
        .call(
          "AMC-TransferenciasInventario-updateTransferenciaInventario",
          datos
        )
        .then((result) => {
          if (result) {
            if (result.data.ok) {
              this.$pgsHelper.writeLog({
                Evento: "INVENTARIO_TRASPASO_EDIT",
                Mensaje: `Documento de traspaso editado: ${this.idDocumento}`,
                reference: { id: this.idDocumento, tp: "INV-TraspasoMercancia", al: "TRASPASO INVENTARIO" },
                data: datos
              });
              this.getDocumentInfo(this.idDocumento);
              this.saving = false;
              if (sendInv) {
                this.sendInventario();
              }
            } else {
              this.$swal("Error saving Documentos", result.data.error, "error");
            }
          }
        })
        .catch((error) => {
          this.saving = false;
          this.$swal({
            title: "Error",
            text: `Request failed: ${error}`,
            icon: "error",
          });
        });
    },
    deleteInsumo() {
      this.selection.forEach((selectProd) => {
        let prod = this.$pgsHelper.getById(
          this.productsDocument,
          "id",
          selectProd.id
        );
        if (prod) {
          this.productsDocument.splice(prod.idx, 1);
        }
      });
    },
  },
  computed: {
    renderedData() {
      const key = this.tableKey;
      const presArr = Object.entries(this.reg).map(([Id, info]) => ({
        Id,
        ...info,
      }));
      const ordered = this.$_.orderBy(presArr, "pi", "asc");
      const result = [];
      let lastPi = null;

      for (const pres of ordered) {
        if (pres.pi !== lastPi) {
          const prod = this.productsDocument.find((p) => p.id === pres.pi);
          if (prod) {
            const prd = this.$pgsHelper.getById(
              this.$store.getters.getProductos,
              "id",
              prod.id
            );

            const tipoConfig = {
              Producto: {
                icon: "mdi-cube-outline",
                label: "Producto",
                color: "green",
              },
              "AYB-ICompuesto": {
                icon: "mdi-flask-outline",
                label: "Subreceta",
                color: "orange",
              },
            };

            const defaultConfig = {
              icon: "mdi-truck-delivery",
              label: "Insumo",
              color: "primary",
            };

            const {
              icon: icn,
              label: tp,
              color: clr,
            } = tipoConfig[prd?.Tipo] ?? defaultConfig;

            result.push({
              type: "header",
              nm: prod.nm,
              ct: prod.cn,
              Id: pres.pi,
              tp: tp,
              icn: icn,
              clr: clr,
            });
          }
          lastPi = pres.pi;
        }
        result.push({ ...pres, type: null });
      }
      return result;
    },

    modulos() {
      let dep = [
        /*{ value: "", text: "Seleccione un modulo" }*/
      ];
      let departamentos = this.$store.getters.getPDVDepartamentos;
      departamentos.forEach((element) => {
        dep.push({ value: element.id, text: element.Nombre });
      });
      return dep;
    },
    origenes() {
      let ori = this.$store.getters.getCentrosConsumo;
      return ori;
    },
  },
  watch: {},
  mounted() {
    this.columnDefs = [
      { field: "nm", headerName: "Nombre" },
      { field: "cn", headerName: "Cantidad", editable: true },
    ];
  },
  created() {
    if(this.idDocumento) {
      this.title = "Edición de documento : " + this.idDocumento;
      this.getDocumentInfo(this.idDocumento);
    } else if (
      this.deptoOrigen &&
      this.almacenOrigen &&
      this.deptoDestino &&
      this.almacenDestino
    ) {
      this.dataDoc.from.dpt = this.deptoOrigen;
      this.dataDoc.from.amc = this.almacenOrigen;
      this.dataDoc.to.dpt = this.deptoDestino;
      this.dataDoc.to.amc = this.almacenDestino;
    }
    // if (this.idDocumento) {
      // this.title = "Edición de documento : " + this.idDocumento;
      // this.getDocumentInfo(this.idDocumento);
    // }
  },
};
</script>
